import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconShoppingCart } from '@redteclab/icons'

import { LoadingButton } from 'base-ui'

import {
  cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier,
  cartIntermediateAccessorGetSellerData,
  useProductAddToCart,
} from '../../../../../cart-intermediate'
import { EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA } from '../../../../../experiments/eventTracking'
import { usePdpBuyTogetherRecommendationTracking } from '../../../../../experiments/experiment-pdp-buy-together-recommendation/usePdpBuyTogetherRecommendationTracking'
import { useExperimentRemoveBannersTracking } from '../../../../../experiments/experiment-remove-banners/useExperimentRemoveBannersTracking'
import { ProductAddToCartAppRedemption } from '../../../ProductAppRedemption/ProductAddToCartAppRedemption'
import { useProductAppRedemptionEligibility } from '../../../ProductAppRedemption/useProductAppRedemptionEligibility'
import { useProductPageContext } from '../../../productPageContext'
import { useProductPrescriptionDrawerControls } from '../../../ProductPrescriptionDrawer/useProductPrescriptionDrawerControls'
import { useProductPageUdpTrackingAddToCart } from '../../hooks/useProductPageUdpTrackingAddToCart'
import { ProductAddToCartPrescriptionRedemption } from '../../ProductAddToCartGroup/ProductAddToCartCta/ProductAddToCartPrescriptionRedemption'
import { useBuildAddToCartParams } from '../../ProductAddToCartGroup/ProductAddToCartCta/useBuildAddToCartParams'
import { useProductAddToCartCtaShouldShowSelectErxModal } from '../../ProductAddToCartGroup/ProductAddToCartCta/useProductAddToCartCtaShouldShowSelectErxModal'

export const ProductBuyBoxStickyAddToCart: FC = () => {
  const { disableCta } = useBuildAddToCartParams()
  const prescriptionRedemptionControls = useProductPrescriptionDrawerControls()
  const shouldOpenPrescriptionRedemption =
    useProductAddToCartCtaShouldShowSelectErxModal()
  const showAppRedemption = useProductAppRedemptionEligibility()

  const {
    attributeSelectionControls,
    currentOffer,
    currentPrescriptionType,
    product,
    quantitySelectionControls: { quantity },
  } = useProductPageContext()

  const redpoints = currentOffer
    ? cartIntermediateAccessorGetRedPointsRedPointsAmountWithMultiplier(
        currentOffer,
      )
    : 0
  const sellerData = cartIntermediateAccessorGetSellerData(currentOffer)

  const { handleAddToCart, isMutating } = useProductAddToCart({
    currentPrescriptionType,
    offerId: currentOffer?.offerId,
    product,
    quantity,
    redpoints,
    sellerData,
    source: 'pdp-sticky',
  })
  const handleUdpTrackingAddToCart = useProductPageUdpTrackingAddToCart()

  const experimentRemoveBannersTracking = useExperimentRemoveBannersTracking(
    EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
  )
  const trackPdpBuyTogetherRecommendationAddToCartButton =
    usePdpBuyTogetherRecommendationTracking(
      EXPERIMENTS_TRACK_EVENT_PDP_ADD_TO_CART_CTA,
      { currentOffer, product },
    )

  const handleClick = (): void => {
    if (shouldOpenPrescriptionRedemption) {
      if (
        !attributeSelectionControls.attributeNotSelectedErrorControls.validateWhenAddingToCart()
      ) {
        return
      }

      prescriptionRedemptionControls.handleOpen()

      return
    }
    void handleAddToCart()
    handleUdpTrackingAddToCart()
    experimentRemoveBannersTracking()
    trackPdpBuyTogetherRecommendationAddToCartButton()
  }

  return (
    <>
      {showAppRedemption ? (
        <div className="min-w-0 flex-1 tablet:w-96">
          <ProductAddToCartAppRedemption
            disabled={disableCta}
            isSticky
            loading={isMutating}
            onPaperClick={handleClick}
          />
        </div>
      ) : (
        <LoadingButton
          className="w-full"
          data-qa-id="add-to-cart-button-sticky"
          disabled={disableCta}
          loading={isMutating}
          loadingPosition="start"
          onClick={(): void => {
            handleClick()
          }}
          startIcon={isMutating ? null : <IconShoppingCart />}
          type="button"
        >
          <FormattedMessage id="general.button.label.addToCart" />
        </LoadingButton>
      )}
      <ProductAddToCartPrescriptionRedemption
        controls={prescriptionRedemptionControls}
      />
    </>
  )
}
